// ============================================
// 엔딩 시스템 - 18개의 다양한 엔딩
// ============================================

import { EndingType, Ending } from '../types';
import { DuckMBTI, calculateMBTIType, DUCK_PERSONALITIES, MBTIType } from './duckMBTI';

// ============================================
// 엔딩 정의
// ============================================

export const ENDINGS: Record<EndingType, Ending> = {
    // ========== 긍정적 엔딩 ==========
    [EndingType.CONQUEST]: {
        type: EndingType.CONQUEST,
        name: '대륙의 지배자',
        description: '오링 제국이 모든 연못을 통일했습니다!',
        epilogue: `
            당신의 지도 아래 오링 제국은 역사상 가장 강대한 왕국이 되었습니다.
            수천 마리의 오리들이 당신의 이름을 높이 부르고,
            모든 연못에 오링왕국의 깃발이 휘날립니다.

            후세의 역사가들은 이 시대를 "황금 오리의 시대"라 부르게 될 것입니다.

            하지만... 정복자의 고독은 누구도 이해하지 못합니다.
            높은 왕좌에서 내려다보는 세상은 때로 너무나 작게 느껴집니다.
        `,
        icon: '👑🌍',
        isPositive: true,
        isSecret: false,
        requirements: '인구 1000+, 오링 5,000,000+'
    },

    [EndingType.PEACE]: {
        type: EndingType.PEACE,
        name: '영원한 평화',
        description: '모든 이가 행복한 유토피아가 완성되었습니다.',
        epilogue: `
            당신의 왕국에는 전쟁도, 굶주림도, 슬픔도 없습니다.
            오리들은 매일 아침 감사의 노래를 부르며 일어나고,
            저녁에는 별빛 아래 춤을 춥니다.

            "꽥꽥의 노래"는 이 시대를 기억하는 가장 아름다운 선율입니다.

            당신은 덕으로 나라를 다스린 전설의 왕으로 기억될 것입니다.
            진정한 지도자는 힘이 아닌 사랑으로 백성을 이끈다는 것을...
            당신이 증명했습니다.
        `,
        icon: '☮️🕊️',
        isPositive: true,
        isSecret: false,
        requirements: '행복도 85+ 200턴 유지'
    },

    [EndingType.GOLDEN_DUCK]: {
        type: EndingType.GOLDEN_DUCK,
        name: '황금 오리의 전설',
        description: '모든 건물을 최대로 발전시킨 전설의 왕국!',
        epilogue: `
            오링왕국은 이제 신화가 되었습니다.
            황금빛 궁전, 보석으로 장식된 연못, 구름까지 닿는 첨탑...

            다른 왕국의 오리들은 순례자가 되어 이 전설의 땅을 방문합니다.
            "저 곳에 가면 모든 꿈이 이루어진다"는 말이 퍼져나갑니다.

            당신은 단순한 왕이 아닌, 건설자이자 예술가이자 몽상가였습니다.
            당신이 만든 이 세계는 영원히 빛날 것입니다.
        `,
        icon: '🦆✨',
        isPositive: true,
        isSecret: false,
        requirements: '모든 건물 최대 레벨'
    },

    [EndingType.SKY_KINGDOM]: {
        type: EndingType.SKY_KINGDOM,
        name: '구름 위의 왕국',
        description: '철새들과 함께 하늘 위 새로운 세계로!',
        epilogue: `
            당신은 대담한 결정을 내렸습니다.
            땅의 연못을 떠나 구름 위의 새로운 세계로...

            오리들은 처음엔 두려워했지만, 당신의 용기에 감화되어 함께 날았습니다.
            구름 사이로 빛나는 무지개 연못, 영원히 따뜻한 태양...

            지상의 오리들은 밤하늘의 별자리를 보며 말합니다.
            "저기, 하늘로 간 오리들의 왕국이 있대."

            당신의 모험심이 새로운 전설을 만들었습니다.
        `,
        icon: '☁️🌈',
        isPositive: true,
        isSecret: false,
        requirements: '하늘로의 여정 스토리 완료'
    },

    [EndingType.SEA_KINGDOM]: {
        type: EndingType.SEA_KINGDOM,
        name: '심해의 제왕',
        description: '바다 건너 신비의 섬에 새 왕국을 세웠습니다!',
        epilogue: `
            험난한 항해 끝에 도착한 신비의 섬...
            그곳은 상상을 초월하는 낙원이었습니다.

            산호로 만든 궁전, 진주를 뿌린 해변, 노래하는 물고기들...
            당신은 바다의 왕으로 새로운 역사를 시작합니다.

            먼 훗날, 고향을 그리워하는 밤이면
            당신은 해변에 앉아 파도 소리를 듣습니다.

            "모험은 끝이 아니라 새로운 시작이다."
        `,
        icon: '🌊👑',
        isPositive: true,
        isSecret: false,
        requirements: '심해 탐험 스토리 완료'
    },

    [EndingType.LEGEND]: {
        type: EndingType.LEGEND,
        name: '전설의 오리',
        description: '수많은 모험을 완수한 전설적인 왕!',
        epilogue: `
            10개 이상의 전설적인 모험을 완수한 당신...
            역사는 당신을 "천 개의 이야기를 가진 오리"라 부릅니다.

            여우와의 전쟁, 하늘로의 여행, 바다의 모험, 황금알의 비밀...
            당신의 이야기는 세대를 넘어 전해질 것입니다.

            어린 오리들은 잠들기 전 이야기를 조릅니다.
            "전설의 오리왕 이야기 해주세요!"

            당신은 영원히 살아있는 전설이 되었습니다.
        `,
        icon: '📚⭐',
        isPositive: true,
        isSecret: false,
        requirements: '5개 이상의 스토리 완료, 1000턴 이상'
    },

    [EndingType.MERCHANT_EMPIRE]: {
        type: EndingType.MERCHANT_EMPIRE,
        name: '상인 제국',
        description: '무역으로 세계를 지배하는 경제 대국!',
        epilogue: `
            당신은 칼이 아닌 오링으로 세계를 정복했습니다.
            모든 무역로가 오링왕국을 통과하고,
            모든 상인이 당신의 화폐로 거래합니다.

            "오링 없이는 거래 없다"는 말이 세계의 격언이 되었습니다.

            부의 힘을 아는 당신은 이제 새로운 고민을 합니다.
            "진정한 풍요란 무엇일까?"

            황금 왕좌에 앉은 당신의 눈에 비친 것은...
            함께 웃는 백성들의 얼굴입니다.
        `,
        icon: '💰🌐',
        isPositive: true,
        isSecret: false,
        requirements: '시장 15(MAX), 항구 5(MAX), 오링 50,000,000+'
    },

    // ========== 중립적 엔딩 ==========
    [EndingType.MIGRATION]: {
        type: EndingType.MIGRATION,
        name: '대이주',
        description: '더 나은 곳을 찾아 왕국 전체가 이동했습니다.',
        epilogue: `
            이곳은 더 이상 살 수 없다고 판단한 당신...
            용감한 결단으로 전체 왕국의 이주를 결정했습니다.

            긴 여정 끝에 찾은 새로운 땅...
            모든 것을 다시 시작해야 하지만, 희망이 있습니다.

            "뿌리 뽑힌 나무도 다시 심으면 자란다."

            당신의 결단이 백성들을 구했습니다.
            새로운 오링왕국이 시작됩니다.
        `,
        icon: '🦆➡️🏝️',
        isPositive: true,
        isSecret: false,
        requirements: '이주 이벤트 선택'
    },

    [EndingType.FOX_ALLIANCE]: {
        type: EndingType.FOX_ALLIANCE,
        name: '여우와의 동맹',
        description: '적과 친구가 되어 새로운 시대를 열었습니다.',
        epilogue: `
            누가 상상이나 했을까요?
            천년의 적 여우와 오리가 동맹을 맺다니...

            처음엔 모두가 불안해했습니다.
            하지만 당신의 외교력이 불가능을 가능으로 만들었습니다.

            이제 여우와 오리는 함께 연못가에서 놉니다.
            "평화는 용서에서 시작된다"는 것을 당신이 증명했습니다.

            역사상 가장 용감한 화해의 악수로 기록될 것입니다.
        `,
        icon: '🦊🤝🦆',
        isPositive: true,
        isSecret: false,
        requirements: '여우 침공 체인 - 외교 루트 완료'
    },

    [EndingType.ASCENSION]: {
        type: EndingType.ASCENSION,
        name: '승천',
        description: '깨달음을 얻어 더 높은 존재가 되었습니다.',
        epilogue: `
            백조의 가르침을 따라 긴 수행의 길을 걸었습니다.
            마침내 깨달음의 순간이 찾아왔습니다.

            "모든 것은 하나이고, 하나는 모든 것이다."

            당신의 육체는 빛으로 변해 하늘로 올라갔습니다.
            백성들은 슬퍼하지 않았습니다.
            당신이 항상 그들과 함께 있음을 알기 때문입니다.

            밤하늘에 새로운 별이 떴습니다.
            오리들은 그것을 "왕의 별"이라 부릅니다.
        `,
        icon: '✨🌟',
        isPositive: true,
        isSecret: false,
        requirements: '백조 만남 + 행복도 80+'
    },

    // ========== 부정적 엔딩 ==========
    [EndingType.EXTINCTION]: {
        type: EndingType.EXTINCTION,
        name: '멸종',
        description: '왕국의 모든 오리가 사라졌습니다...',
        epilogue: `
            텅 빈 연못에 바람만 분다.
            한때 꽥꽥거리던 소리가 가득했던 이곳이
            이제는 침묵뿐입니다.

            무엇이 잘못되었을까요?
            가뭄? 전쟁? 전염병?
            아니면... 지도자의 판단 미스?

            버려진 둥지들 사이로
            마지막 깃털이 바람에 날립니다.

            오링왕국은 역사 속으로 사라졌습니다.

            [GAME OVER]
        `,
        icon: '💀🦆',
        isPositive: false,
        isSecret: false,
        requirements: '인구 0'
    },

    [EndingType.REBELLION]: {
        type: EndingType.REBELLION,
        name: '반란',
        description: '백성들이 폭군을 몰아냈습니다.',
        epilogue: `
            "더 이상은 못 참아!"
            분노한 오리들이 궁전으로 몰려왔습니다.

            당신은 모든 것을 잃었습니다.
            왕관도, 보물도, 명예도...

            어둠 속으로 쫓겨난 당신...
            이제 혼자입니다.

            어디선가 비웃는 소리가 들립니다.
            "폭군의 말로란 이런 것이지."

            권력은 백성에게서 나온다는 것을...
            너무 늦게 깨달았습니다.

            [GAME OVER]
        `,
        icon: '😠🔥',
        isPositive: false,
        isSecret: false,
        requirements: '행복도 0'
    },

    [EndingType.FOX_CONQUEST]: {
        type: EndingType.FOX_CONQUEST,
        name: '여우의 정복',
        description: '왕국이 여우에게 점령당했습니다.',
        epilogue: `
            여우 군단이 연못을 에워쌌습니다.
            더 이상 저항은 무의미했습니다.

            당신은 무릎을 꿇었습니다.
            왕관이 땅에 굴렀습니다.

            이제 오리들은 여우의 신하가 되어
            매일 공물을 바쳐야 합니다.

            "우리가 그렇게 약했던가..."
            후회해도 이미 늦었습니다.

            오링왕국의 역사는 여기서 끝났습니다.

            [GAME OVER - 정복됨]
        `,
        icon: '🦊👑',
        isPositive: false,
        isSecret: false,
        requirements: '여우 침공 체인 - 항복'
    },

    [EndingType.STARVATION]: {
        type: EndingType.STARVATION,
        name: '대기근',
        description: '굶주림에 왕국이 무너졌습니다.',
        epilogue: `
            식량이 바닥났습니다.
            창고는 텅 비었고, 밭은 말라붙었습니다.

            굶주린 오리들의 처절한 울음소리가
            밤새 연못에 울려 퍼집니다.

            하나 둘 쓰러지는 백성들...
            왕도 예외는 아닙니다.

            "먹을 것이... 조금만..."
            마지막 말을 남기고...

            풍요로웠던 왕국의 비참한 최후입니다.

            [GAME OVER]
        `,
        icon: '🍂💀',
        isPositive: false,
        isSecret: false,
        requirements: '기근 이벤트 실패'
    },

    [EndingType.PLAGUE]: {
        type: EndingType.PLAGUE,
        name: '역병',
        description: '치명적인 전염병이 왕국을 휩쓸었습니다.',
        epilogue: `
            검은 깃털 병이 퍼지기 시작했을 때
            이미 늦었습니다.

            하루가 다르게 빈 둥지가 늘어갔습니다.
            치료법은 없었습니다.

            마지막 생존자가 쓴 기록:
            "모두 떠났습니다. 저만 남았습니다.
            왜 저만 살아남았을까요...
            이 기록이라도 남겨야..."

            여기서 기록은 끊깁니다.

            [GAME OVER]
        `,
        icon: '🦠☠️',
        isPositive: false,
        isSecret: false,
        requirements: '전염병 이벤트 실패'
    },

    // ========== 숨겨진 엔딩 ==========
    [EndingType.TRUE_ENDING]: {
        type: EndingType.TRUE_ENDING,
        name: '진실의 끝',
        description: '모든 비밀을 밝히고 진정한 결말에 도달했습니다.',
        epilogue: `
            긴 여정의 끝에서 당신은 마침내 진실을 알았습니다.

            이 세계는... 누군가의 꿈이었습니다.
            당신도, 백성들도, 이 모든 모험도...

            하지만 그것이 무슨 상관일까요?
            꿈속의 행복도 진짜 행복입니다.
            꿈속의 사랑도 진짜 사랑입니다.

            당신은 눈을 감습니다.
            다시 꿈을 꾸기 위해.

            새로운 오링왕국이 시작됩니다.
            영원히.

            [ TRUE END ]

            플레이해주셔서 감사합니다.
        `,
        icon: '🔮💫',
        isPositive: true,
        isSecret: true,
        requirements: '캐릭터 5명+ 만남 + 스토리 3개+ 완료 + 깨달음'
    },

    [EndingType.SECRET_DUCK]: {
        type: EndingType.SECRET_DUCK,
        name: '비밀의 오리',
        description: '전설 속 황금 오리를 찾았습니다!',
        epilogue: `
            전설은 사실이었습니다.
            연못 가장 깊은 곳, 빛도 닿지 않는 그곳에
            황금빛 오리가 잠들어 있었습니다.

            "드디어 나를 찾았구나..."

            황금 오리는 한 가지 소원을 들어준다고 합니다.
            당신은 무엇을 빌었을까요?

            그것은 비밀입니다.
            영원히.

            [ SECRET END ]
        `,
        icon: '🦆🌟',
        isPositive: true,
        isSecret: true,
        requirements: '황금 오리 전설 듣기 + 비밀 오리 체인 완료'
    },

    [EndingType.TIME_LOOP]: {
        type: EndingType.TIME_LOOP,
        name: '시간의 고리',
        description: '시간이 반복되기 시작합니다...',
        epilogue: `
            이상합니다.
            분명 이 순간을 전에도 겪은 것 같은...

            데자뷰?
            아니, 이건 진짜입니다.

            시간이 되돌아가고 있습니다.
            다시 1턴으로.
            다시 작은 연못으로.

            "또 시작이군..."

            몇 번째 반복일까요?
            아무도 모릅니다.

            어쩌면 당신은 영원히 이 순환 속에...

            [ LOOP END - ??? ]
        `,
        icon: '🔄⏰',
        isPositive: false,
        isSecret: true,
        requirements: '시간 역설 플래그'
    }
};

// ============================================
// MBTI 기반 엔딩 추천
// ============================================

export function getRecommendedEndings(mbti: DuckMBTI): EndingType[] {
    const type = calculateMBTIType(mbti);
    const personality = DUCK_PERSONALITIES[type];

    return personality.compatibleEndings.map(name => {
        const endingKey = name as keyof typeof EndingType;
        return EndingType[endingKey];
    }).filter(Boolean);
}

// ============================================
// 엔딩 달성률 계산
// ============================================

export function getEndingProgress(
    achievedEndings: EndingType[]
): { achieved: number; total: number; percentage: number } {
    const total = Object.keys(ENDINGS).length;
    const achieved = achievedEndings.length;
    const percentage = Math.round((achieved / total) * 100);

    return { achieved, total, percentage };
}

// ============================================
// 엔딩 화면 데이터 생성
// ============================================

export interface EndingScreenData {
    ending: Ending;
    mbtiType: MBTIType;
    mbtiName: string;
    mbtiDescription: string;
    playTime: string;
    statistics: {
        label: string;
        value: string | number;
    }[];
}

export function generateEndingScreen(
    endingType: EndingType,
    mbti: DuckMBTI,
    stats: {
        totalTurns: number;
        totalMoney: number;
        maxPopulation: number;
        eventsTriggered: number;
        chainsCompleted: number;
        playTimeSeconds: number;
    }
): EndingScreenData {
    const ending = ENDINGS[endingType];
    const mbtiType = calculateMBTIType(mbti);
    const personality = DUCK_PERSONALITIES[mbtiType];

    // 플레이 시간 포맷
    const hours = Math.floor(stats.playTimeSeconds / 3600);
    const minutes = Math.floor((stats.playTimeSeconds % 3600) / 60);
    const playTime = hours > 0 ? `${hours}시간 ${minutes}분` : `${minutes}분`;

    return {
        ending,
        mbtiType,
        mbtiName: personality.name,
        mbtiDescription: personality.description,
        playTime,
        statistics: [
            { label: '총 턴 수', value: stats.totalTurns.toLocaleString() },
            { label: '획득한 총 오링', value: stats.totalMoney.toLocaleString() },
            { label: '최대 인구', value: stats.maxPopulation.toLocaleString() },
            { label: '발생한 이벤트', value: stats.eventsTriggered },
            { label: '완료한 스토리', value: stats.chainsCompleted },
            { label: '플레이 시간', value: playTime }
        ]
    };
}

// ============================================
// 엔딩 크레딧
// ============================================

export const ENDING_CREDITS = `
╔════════════════════════════════════════╗
║                                        ║
║           오 링 왕 국                   ║
║         O-Ring Kingdom                 ║
║                                        ║
║     플레이해주셔서 감사합니다!           ║
║                                        ║
╠════════════════════════════════════════╣
║                                        ║
║  🦆 게임 디자인 & 개발                  ║
║     Claude AI                          ║
║                                        ║
║  🎨 아트 디렉션                         ║
║     Unicode Emoji Consortium           ║
║                                        ║
║  🎵 사운드                              ║
║     꽥꽥 (Your Imagination)            ║
║                                        ║
║  💝 스페셜 땡스                         ║
║     플레이어 여러분                      ║
║     모든 오리들                          ║
║                                        ║
╠════════════════════════════════════════╣
║                                        ║
║  "모든 위대한 왕국은                    ║
║   작은 연못에서 시작됩니다"             ║
║                                        ║
║              - 현자 오리               ║
║                                        ║
╚════════════════════════════════════════╝
`;
